import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { publicApi } from './api'
import type { Locale } from './types'

/**
 * Query keys, defined once.
 *
 * They have to match exactly between the prerender script (which fills the
 * cache at build time) and the browser (which reads it on hydration) — a typo
 * in one place would silently cause a refetch and a flash of loading state on
 * first paint.
 */
export const queryKeys = {
  categories: (locale: Locale) => ['categories', locale] as const,
  artworks: (locale: Locale, category: string | undefined) =>
    ['artworks', locale, category ?? 'all'] as const,
  artwork: (locale: Locale, ref: string) => ['artwork', locale, ref] as const,
  page: (locale: Locale, slug: string) => ['page', locale, slug] as const,
}

/** How many pieces one "show more" adds. */
export const PAGE_SIZE = 24

export function useCategories(locale: Locale) {
  return useQuery({
    queryKey: queryKeys.categories(locale),
    queryFn: ({ signal }) => publicApi.categories(locale, signal),
    select: (data) => data.items,
  })
}

/**
 * The gallery feed, page by page.
 *
 * Infinite rather than paginated because the visitor is browsing pictures, not
 * looking something up: asking them to pick page 4 of 11 interrupts that.
 */
export function useArtworks(locale: Locale, category?: string) {
  return useInfiniteQuery({
    queryKey: queryKeys.artworks(locale, category),
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) =>
      publicApi.artworks({ locale, category, limit: PAGE_SIZE, offset: pageParam }, signal),
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.offset + lastPage.items.length
      return loaded < lastPage.total ? loaded : undefined
    },
  })
}

/** `ref` is the URL segment: "42-kit-bandyt". The API resolves it by number. */
export function useArtwork(locale: Locale, ref: string) {
  return useQuery({
    queryKey: queryKeys.artwork(locale, ref),
    queryFn: ({ signal }) => publicApi.artwork(ref, locale, signal),
    enabled: Boolean(ref),
  })
}

export function usePage(locale: Locale, slug: string) {
  return useQuery({
    queryKey: queryKeys.page(locale, slug),
    queryFn: ({ signal }) => publicApi.page(slug, locale, signal),
  })
}
